import { type PointerEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SIDEBAR } from '../appTemplate.consts';
import { PeekPointerHandlers, SidebarPeek } from '../appTemplate.types';
import { useTransientState } from './useTransientState';

const isMousePointer = (event: PointerEvent) => event.pointerType === 'mouse';

const isLeavingThroughTop = (event: PointerEvent) =>
  event.clientY <= event.currentTarget.getBoundingClientRect().top;

interface UseSidebarPeekParams {
  isEnabled: boolean;
  isExpanded: boolean;
}

export const useSidebarPeek = ({ isEnabled, isExpanded }: UseSidebarPeekParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFloating, setIsFloating] = useState(false);
  const [isUndocked, setIsUndocked] = useState(false);
  const isOverTriggerRef = useRef(false);
  const intentTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { isActive: isDocking, trigger: startDocking } = useTransientState(
    SIDEBAR.transitionDuration,
  );

  const clearTimers = () => {
    clearTimeout(intentTimerRef.current);
    clearTimeout(settleTimerRef.current);
  };

  const openPeek = () => {
    clearTimers();
    setIsUndocked(false);
    setIsFloating(true);
    setIsOpen(true);
  };

  const undockPeek = () => {
    if (!isOverTriggerRef.current) {
      return false;
    }

    clearTimers();
    setIsUndocked(true);
    setIsFloating(true);
    setIsOpen(true);
    return true;
  };

  const closePeek = () => {
    clearTimers();
    setIsOpen(false);
    settleTimerRef.current = setTimeout(() => setIsFloating(false), SIDEBAR.peekTransitionDuration);
  };

  useLayoutEffect(() => {
    if (isEnabled) {
      return;
    }

    if (isOpen && isExpanded) {
      startDocking();
    }

    clearTimeout(intentTimerRef.current);
    clearTimeout(settleTimerRef.current);
    setIsOpen(false);
    setIsFloating(false);
  }, [isEnabled, isOpen, isExpanded, startDocking]);

  useEffect(
    () => () => {
      clearTimeout(intentTimerRef.current);
      clearTimeout(settleTimerRef.current);
    },
    [],
  );

  const handlePointerEnter = (event: PointerEvent) => {
    if (!isEnabled || !isMousePointer(event)) {
      return;
    }

    clearTimeout(intentTimerRef.current);

    if (isOpen) {
      return;
    }

    if (isFloating) {
      openPeek();
      return;
    }

    intentTimerRef.current = setTimeout(openPeek, SIDEBAR.peekOpenDelay);
  };

  const scheduleClose = (event: PointerEvent, delay: number) => {
    if (!isMousePointer(event)) {
      return;
    }

    clearTimeout(intentTimerRef.current);

    if (isOpen) {
      intentTimerRef.current = setTimeout(closePeek, delay);
    }
  };

  const peekTrigger: PeekPointerHandlers = {
    onPointerEnter: (event) => {
      isOverTriggerRef.current = isMousePointer(event);
      handlePointerEnter(event);
    },
    onPointerLeave: (event) => {
      isOverTriggerRef.current = false;
      scheduleClose(event, SIDEBAR.peekGapCrossingDelay);
    },
  };

  const peek: SidebarPeek = {
    isOpen: isEnabled && isOpen,
    isFloating: isEnabled && isFloating,
    isDocking: !isEnabled && (isDocking || (isOpen && isExpanded)),
    isUndocked: isEnabled && isFloating && isUndocked,
    onPointerEnter: handlePointerEnter,
    onPointerLeave: (event) =>
      scheduleClose(
        event,
        isLeavingThroughTop(event) ? SIDEBAR.peekGapCrossingDelay : SIDEBAR.peekPanelLeaveDelay,
      ),
  };

  return { peek, peekTrigger, closePeek, undockPeek };
};
